// @flow

import React, { type Node } from 'react';
import { useUniqueId, useMustShow } from '@tectonics/hooks';
import { InputMask, formatValueWithMask, type ArrayMask } from '@tectonics/components/input-mask';
import { useUserInputAsTextFieldWithFormatting } from '@tectonics/prs/user-input-values';
import { useFieldMetadata } from '@tectonics/prs/form';
import { type UserInputValidationError } from '@tectonics/prs/validation';
import { getPhoneNumberNormalizer } from '@tectonics/prs/customer/normalize';
import { SimpleTextErrorMessage } from '@tectonics/look-and-feel/commons/validation-error-message';
import { type Country } from '@tectonics/prs/country';

import { Field } from '../../../molecules';
import { Input, Label, Empty } from '../../../atoms';

export function FieldBrazilianPhoneNumber(): Node {
  const mustShow = useMustShow('brazilianPhoneNumber');
  const inputId = useUniqueId();
  const phoneNumberNormalizer = getPhoneNumberNormalizer('br');
  const [value, handleChange] = useUserInputAsTextFieldWithFormatting('brazilianPhoneNumber', formatRaw, phoneNumberNormalizer);
  const labelMessage = 'Telefone';
  const { submitting, validationError, formHasBeenSent } = useFieldMetadata('brazilianPhoneNumber');

  if (!mustShow)
    return <Empty />;

  return (
    <Field
      data-test-name="customer-phone-number"
      label={<Label htmlFor={inputId}>{labelMessage}</Label>}
      showFloatingLabel={!!value}
      hasError={validationError && formHasBeenSent}
      error={<SimpleTextErrorMessage getMessage={getMessage} validationError={validationError} />}
    >
      <Input
        as={InputMask}
        data-error={formHasBeenSent && validationError ? 'error' : null}
        data-secret
        fashion={formHasBeenSent && validationError ? 'error' : null}
        id={inputId}
        mask={mask}
        onChange={handleChange}
        placeholder={labelMessage}
        readOnly={submitting}
        type="tel"
        value={value}
        name="customer-phone-number"
      />
    </Field>
  );
}

function getMessage(validationError: UserInputValidationError, country: Country): ?string {
  switch (validationError.id) {
    case 'empty':
      return 'Preencha seu telefone';
    default:
      return 'Telefone inválido';
  }
}

function formatRaw(rawValue: string): string {
  return formatValueWithMask(rawValue, getMaskForRawValue(rawValue));
}

function mask(formattedValue: string): ArrayMask {
  if (formattedValue.length <= 14)
    return TEN_DIGITS_PATTERN;
  else
    return ELEVEN_DIGITS_PATTERN;
}

function getMaskForRawValue(rawValue: string): ArrayMask {
  if (rawValue.length <= 10)
    return TEN_DIGITS_PATTERN;
  else
    return ELEVEN_DIGITS_PATTERN;
}

const TEN_DIGITS_PATTERN = [
  '(', /[0-9]/, /[0-9]/, ')', ' ',
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, '-',
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
];

const ELEVEN_DIGITS_PATTERN = [
  '(', /[0-9]/, /[0-9]/, ')', ' ',
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, '-',
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
];
